import React, { useEffect, useId, useState } from "react";
import { useMergeCandidates } from "@/state/hooks/useVisitMerges";
import { CandidateRow } from "@/common/lib/api/visitMerges";
import { COPY, formatVisitWhen, reasonText } from "./copy";
import { samePatientName } from "./visitCompare";

type Props = {
    visitId: string;
    patientName: string;
    visitTime: string;
    selectedId: string | null;
    /** query: the search the row was picked from, so the dialog can keep watching the same list. */
    onSelect: (id: string, query: string) => void;
    pimsName: string;
};

/** Typing waits this long before it becomes a candidates request; the input shows every keystroke. */
export const SEARCH_DEBOUNCE_MS = 250;

const GROUPS: { group: CandidateRow["group"]; title: string }[] = [
    { group: "likely", title: COPY.groupLikely },
    { group: "recent", title: COPY.groupRecent },
];

export function VisitPickerList({ visitId, patientName, visitTime, selectedId, onSelect, pimsName }: Props) {
    const [q, setQ] = useState("");
    const [query, setQuery] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setQuery(q), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [q]);
    const { data, isLoading, isError, refetch } = useMergeCandidates(visitId, query);
    const rows = data?.rows ?? [];
    const radioName = useId();

    const renderRow = (r: CandidateRow) => {
        const whyText = r.reason ? reasonText(r.reason, pimsName) : null;
        const descId = `${radioName}-${r.visit.id}-why`;
        const selected = selectedId === r.visit.id;
        return (
            <label
                key={r.visit.id}
                className={`grid grid-cols-[18px_1fr_auto] items-center gap-3 rounded-lg border px-3 py-2.5 ${
                    !r.eligible
                        ? "border-dashed bg-gray-50"
                        : selected
                          ? "border-2 border-max-900 bg-max-25"
                          : "border-gray-200"
                } ${r.group === "likely" && r.eligible && !selected ? "bg-tropicana-25" : ""}`}
            >
                <input
                    type="radio"
                    name={radioName}
                    className="h-4 w-4 accent-max-900"
                    disabled={!r.eligible}
                    checked={selected}
                    onChange={() => onSelect(r.visit.id, query)}
                    aria-describedby={whyText ? descId : undefined}
                />
                <span className="flex flex-col gap-0.5">
                    <span className={`text-sm font-bold ${r.eligible ? "text-gray-900" : "text-gray-600"}`}>
                        {r.visit.patientName ?? "(blank)"} · {formatVisitWhen(r.visit.scheduledAt, r.visit.createdAt)}
                    </span>
                    {whyText ? (
                        <span
                            id={descId}
                            className={`text-xs ${r.reason === "recording_active" ? "text-blaze-900" : "text-gray-500"}`}
                        >
                            {whyText}
                        </span>
                    ) : (
                        <span className="text-xs text-gray-500">
                            {r.visit.clipCount === 0 && r.visit.docCount === 0
                                ? "Empty"
                                : COPY.contentCounts(r.visit.clipCount, r.visit.docCount)}
                        </span>
                    )}
                </span>
                {r.visit.linked && (
                    <span className="rounded-md bg-max-50 px-2 py-px text-[11.5px] font-semibold text-max-950">
                        {pimsName}
                    </span>
                )}
            </label>
        );
    };

    const renderGroup = ({ group, title }: (typeof GROUPS)[number]) => {
        const inGroup = rows.filter((r) => r.group === group);
        // Same patient first, so a mis-tap on a neighbouring patient's visit is less likely. Stable otherwise.
        const list =
            group === "recent"
                ? [
                      ...inGroup.filter((r) => samePatientName(r.visit.patientName, patientName)),
                      ...inGroup.filter((r) => !samePatientName(r.visit.patientName, patientName)),
                  ]
                : inGroup;
        if (list.length === 0) return null;
        const titleId = `${radioName}-${group}`;
        return (
            <div key={group} role="radiogroup" aria-labelledby={titleId} className="flex flex-col gap-2">
                <p id={titleId} className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    {title}
                </p>
                {list.map(renderRow)}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                {COPY.pickerLabel(patientName, visitTime)}
            </p>
            <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={COPY.pickerPlaceholder}
                aria-label="Search visits"
                className="h-10 w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-max-700 focus:ring-max-700"
            />
            {/* The list scrolls, not the dialog, so the footer stays on screen however many visits there are. */}
            <div data-testid="visit-picker-scroll" className="flex max-h-[45vh] flex-col gap-3 overflow-y-auto">
                {GROUPS.map(renderGroup)}
            </div>
            {!isLoading && rows.length === 0 && isError && (
                <div role="alert" className="flex items-center gap-3 text-sm text-blaze-900">
                    {COPY.pickerFailed}
                    <button
                        type="button"
                        className="rounded-md px-2.5 py-1 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                        onClick={() => void refetch()}
                    >
                        {COPY.retry}
                    </button>
                </div>
            )}
            {!isLoading && rows.length === 0 && !isError && <p className="text-sm text-gray-500">{COPY.pickerEmpty}</p>}
        </div>
    );
}
